import React, { useRef, useState } from 'react';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../../../services/assistant-api';

import useUser from '../../../../../hooks/useUser';

import { generateUUID } from '../../../../../helpers/generateUUID';

import { STATUS } from '../../../../../constants/statuses';
import { CLEAR_API_PREVIEW_ID, TEST_API_ROLE, TEST_VIEW } from '../../../../../constants/bot';

import ErrorAlert from '../../../../../design-system/ErrorAlert/ErrorAlert';
import ChatInputBox from '../ChatInputBox/ChatInputBox';
import ApiPreviewMessages from '../ApiPreviewMessages/ApiPreviewMessages';

const ApiPreview = ({ botId, botName, isVisible, panelContainerRef, isInstructionEditing }) => {
    const [apiPreviewMessages, setApiPreviewMessages] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const lastSubmittedQuery = useRef('');
    const controller = useRef(new AbortController());

    const { user } = useUser();

    const submitQuery = async (query) => {
        const apiResponseId = generateUUID();
        lastSubmittedQuery.current = query;

        controller.current?.abort();
        controller.current = new AbortController();

        try {
            setIsLoading(true);
            const submittedQuery = {
                id: generateUUID(),
                query,
                role: TEST_API_ROLE.user,
            };
            const apiResponse = {
                id: apiResponseId,
                status: STATUS.pending,
                response: null,
                role: TEST_API_ROLE.api,
                customBot: { id: botId, name: botName },
            };
            setApiPreviewMessages([submittedQuery, apiResponse]);

            const requestBody = {
                query,
                origin: 'test',
                organization: user.organization.id,
            };

            const { data } = await client.post(
                API.ROUTES.assistant.customBot + botId + '/query/',
                requestBody,
                {
                    signal: controller.current.signal,
                }
            );

            updateMessage({
                id: apiResponseId,
                updatedMessage: {
                    status: STATUS.success,
                    response: data,
                },
            });
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
            if (e?.message === 'canceled') {
                return;
            }

            setErrorAlert({
                message: 'Oops! Something went wrong. Please reload and try again.',
            });
            updateMessage({
                id: apiResponseId,
                updatedMessage: { status: STATUS.failure },
            });
        }
    };

    const handleRegenerate = async () => {
        await submitQuery(lastSubmittedQuery.current);
    };

    function updateMessage({ id, updatedMessage }) {
        setApiPreviewMessages((prevData) =>
            prevData.map((message) =>
                message.id === id
                    ? {
                          ...message,
                          ...updatedMessage,
                      }
                    : message
            )
        );
    }

    const clearApiPreview = () => {
        setApiPreviewMessages([]);
        lastSubmittedQuery.current = '';

        controller.current?.abort();
    };

    const areMessages = !!apiPreviewMessages?.length;

    const containerClassName = classNames(
        'w-full max-w-[1010px] mx-auto min-h-full',
        !areMessages && 'h-full',
        areMessages && 'pb-6'
    );

    const isSendButtonDisabled = isLoading || isInstructionEditing;

    return (
        <>
            <div className={`flex-1 overflow-hidden ${!isVisible && 'hidden'}`}>
                <div className="w-full h-full overflow-y-auto">
                    <div className="px-5 3xl:px-8 pt-5 pb-6 h-full">
                        <div className={containerClassName}>
                            {areMessages && (
                                <div className="flex flex-col gap-6">
                                    {apiPreviewMessages.map((message) => (
                                        <ApiPreviewMessages
                                            key={message.id}
                                            message={message}
                                            handleRegenerate={handleRegenerate}
                                            isRegenerateDisabled={isSendButtonDisabled}
                                        />
                                    ))}
                                </div>
                            )}

                            {!areMessages && (
                                <div className="px-5 sm:px-8 h-4/5 flex items-center justify-center">
                                    <p className="font-heading-regular text-heading-regular-m text-neutral-500 text-center">
                                        Submit a query to {botName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <button
                id={CLEAR_API_PREVIEW_ID}
                className="hidden outline-0"
                onClick={clearApiPreview}
            >
                Clear API Preview
            </button>

            <ChatInputBox
                isVisible={isVisible}
                botName={botName}
                view={TEST_VIEW.api}
                containerRef={panelContainerRef}
                submitChatMessage={submitQuery}
                isLoading={isLoading}
                isSendButtonDisabled={isSendButtonDisabled}
            />

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </>
    );
};

export default ApiPreview;
